import type { PayoutStatementLineView, PayoutStatementView } from '@/api/generated';
import { formatCivilMonth } from '@/i18n/format';

export interface PaymentGroup {
  paymentRef: string;
  lines: PayoutStatementLineView[];
  retail: number;
  wholesale: number;
  margin: number;
  /** Some line in the group carries the amount; else the total is missing, not zero (RPT-005). */
  hasRetail: boolean;
  hasWholesale: boolean;
  hasMargin: boolean;
  excludesMissing: boolean;
}

export function isSyntheticAffiliateLine(line: PayoutStatementLineView): boolean {
  return line.kind === 'AFFILIATE_COMMISSION';
}

/**
 * Statements split by currency, in first-seen order — the payable bars compare heights only within
 * one currency (H §6.3: never across currencies).
 */
export function byCurrency(stmts: PayoutStatementView[]): Array<[string, PayoutStatementView[]]> {
  const groups = new Map<string, PayoutStatementView[]>();
  for (const stmt of stmts) {
    const currency = stmt.currency;
    groups.set(currency, [...(groups.get(currency) ?? []), stmt]);
  }
  return [...groups.entries()];
}

export function groupByPayment(lines: PayoutStatementLineView[]): PaymentGroup[] {
  const groups = new Map<string, PaymentGroup>();
  for (const line of lines) {
    const paymentRef = line.payApiPaymentRef ?? '';
    const existing = groups.get(paymentRef);
    if (existing) {
      existing.lines.push(line);
      existing.excludesMissing ||= line.resellerMarginMinor == null;
      existing.retail += line.retailMinor;
      existing.wholesale += line.wholesaleMinor;
      existing.margin += line.resellerMarginMinor ?? 0;
      existing.hasMargin ||= line.resellerMarginMinor !== undefined;
    } else {
      groups.set(paymentRef, {
        paymentRef,
        excludesMissing: line.resellerMarginMinor == null,
        lines: [line],
        retail: line.retailMinor,
        wholesale: line.wholesaleMinor,
        margin: line.resellerMarginMinor ?? 0,
        hasRetail: true,
        hasWholesale: true,
        hasMargin: line.resellerMarginMinor !== undefined,
      });
    }
  }
  return [...groups.values()].sort((a, b) => {
    const aAt = [...a.lines.map((line) => line.collectedAt ?? '')].sort()[0] ?? '';
    const bAt = [...b.lines.map((line) => line.collectedAt ?? '')].sort()[0] ?? '';
    return aAt.localeCompare(bAt);
  });
}

/**
 * A statement payable to the reseller. Since H §7.4 `payeeKind: "RESELLER"` means **the
 * reseller** (the payee across all its tenants), never the tenant. `/settlements` is already
 * filtered to this reseller, so only an `AFFILIATE` payee is excluded.
 */
export function isResellerRow(stmt: PayoutStatementView): boolean {
  return stmt.payeeKind !== 'AFFILIATE';
}

/** Newest twelve, returned oldest → newest for the bars. */
export function lastTwelve(stmts: PayoutStatementView[]): PayoutStatementView[] {
  const newestFirst = [...stmts].sort((a, b) =>
    b.periodEnd.localeCompare(a.periodEnd),
  );
  return newestFirst.slice(0, 12).reverse();
}

export function newestFirst(stmts: PayoutStatementView[]): PayoutStatementView[] {
  return [...stmts].sort((a, b) => b.periodEnd.localeCompare(a.periodEnd));
}

/** A settlement's month (its `periodStart`) as "August 2026", or `fallback` (DATA-001). */
export function settlementMonth(
  value: string | undefined,
  locale: string,
  fallback: string,
): string {
  if (!value) return fallback;
  try {
    return formatCivilMonth(value, locale);
  } catch {
    return fallback;
  }
}

/**
 * A settlement's affiliate programme lines (TMMRSL-0051, MM-D-106), from the generated contract. Each is a **magnitude** (the wire's sign doesn't matter), or `null` when not sent: bounties
 * and milestone bonuses are debits in the month they become payable; recovered is a credit in the
 * month a clawback is recovered from the affiliate's later commission. Nothing is summed here.
 */
export interface AffiliateProgramme {
  bountiesMinor: number | null;
  milestonesMinor: number | null;
  recoveredMinor: number | null;
}

export function affiliateProgrammeOf(statement: PayoutStatementView): AffiliateProgramme {
  const raw = statement;
  const magnitude = (value: unknown) =>
    typeof value === 'number' && Number.isFinite(value) ? Math.abs(value) : null;
  return {
    bountiesMinor: magnitude(raw.affiliateBountiesMinor),
    milestonesMinor: magnitude(raw.affiliateMilestonesMinor),
    recoveredMinor: magnitude(raw.affiliateRecoveredMinor),
  };
}
